import { apiClient } from './client';

export interface AnalyticsDailyRow {
  date: string;
  channel: string;
  count: number;
}

export interface AnalyticsDailyQualityRow {
  date: string;
  good: number;
  degraded: number;
  poor: number;
  unanalyzed: number;
}

export interface AnalyticsQualityTotals {
  good: number;
  degraded: number;
  poor: number;
  unanalyzed: number;
}

export interface AnalyticsIntentBreakdownRow {
  intentType: string;
  count: number;
  percentage: number;
}

export interface AnalyticsCostTotals {
  promptTokens: number;
  cachedPromptTokens: number;
  completionTokens: number;
  aiCostUsd: number;
  channelCostUsd: number;
  handoffCount: number;
  handoffCostUsd: number;
  totalCostUsd: number;
}

export interface AnalyticsHandoffSummary {
  total: number;
  byChannel: Record<string, number>;
  daily: Array<{ date: string; count: number; costUsd: number }>;
  perHandoffCostUsd: number;
}

export interface AnalyticsDailyCostRow {
  date: string;
  promptTokens: number;
  cachedPromptTokens: number;
  completionTokens: number;
  aiCostUsd: number;
  channelCostUsd: number;
  totalCostUsd: number;
}

export interface AnalyticsCostByModelRow {
  model: string;
  promptTokens: number;
  cachedPromptTokens: number;
  completionTokens: number;
  aiCostUsd: number;
}

export interface AnalyticsLlmRunsTotals {
  runCount: number;
  okCount: number;
  failCount: number;
  promptTokens: number;
  completionTokens: number;
  cachedTokens: number;
  totalTokens: number;
  costUsd: number;
  cacheHitRate: number;
  successRate: number;
}

export interface AnalyticsLlmRunsByModel {
  model: string;
  runCount: number;
  promptTokens: number;
  completionTokens: number;
  cachedTokens: number;
  costUsd: number;
  failRate: number;
  latencyP95Ms: number | null;
}

export interface AnalyticsLlmRunsByRoute {
  routeCanonicalAction: string;
  runCount: number;
  failCount: number;
  costUsd: number;
}

export interface AnalyticsFeedbackByRoute {
  routeCanonicalAction: string;
  up: number;
  down: number;
}

export interface AnalyticsFeedbackSummary {
  totals: { up: number; down: number };
  byRoute: AnalyticsFeedbackByRoute[];
}

export interface AnalyticsLlmRunsDaily {
  date: string;
  runCount: number;
  failCount: number;
  costUsd: number;
}

export interface AnalyticsLlmRunsUnmappedModel {
  modelKey: string;
  updatedAt: string;
  promptPerM: number;
  cachedPromptPerM: number;
  completionPerM: number;
}

export interface AnalyticsLlmRunsByWorkspace {
  workspace: string;
  callCount: number;
}

export interface AnalyticsLlmTelemetry {
  hasData: boolean;
  totals: AnalyticsLlmRunsTotals;
  daily: AnalyticsLlmRunsDaily[];
  byModel: AnalyticsLlmRunsByModel[];
  byRoute: AnalyticsLlmRunsByRoute[];
  byWorkspace?: AnalyticsLlmRunsByWorkspace[];
  feedback?: AnalyticsFeedbackSummary;
  unmappedModels: AnalyticsLlmRunsUnmappedModel[];
}

export interface AnalyticsResponse {
  from: string;
  to: string;
  availableYears: number[];
  daily: AnalyticsDailyRow[];
  totals: {
    byChannel: Record<string, number>;
    total: number;
  };
  quality: {
    daily: AnalyticsDailyQualityRow[];
    totals: AnalyticsQualityTotals;
  };
  intents: {
    totalOccurrences: number;
    breakdown: AnalyticsIntentBreakdownRow[];
  };
  cost: {
    totals: AnalyticsCostTotals;
    daily: AnalyticsDailyCostRow[];
    byModel: AnalyticsCostByModelRow[];
    channelMessageCounts: Record<string, number>;
    handoffs: AnalyticsHandoffSummary;
  };
  llmTelemetry?: AnalyticsLlmTelemetry;
}

export type AnalyticsFilter =
  | { mode: 'days'; days: number }
  | { mode: 'year'; year: number }
  | { mode: 'month'; year: number; month: number };

export const getAnalytics = async (filter: AnalyticsFilter): Promise<AnalyticsResponse> => {
  const params = buildAnalyticsParams(filter);
  const { data } = await apiClient.get<AnalyticsResponse>(`/analytics?${params}`);
  return data;
};

function buildAnalyticsParams(filter: AnalyticsFilter): string {
  if (filter.mode === 'month') return `year=${filter.year}&month=${filter.month}`;
  if (filter.mode === 'year') return `year=${filter.year}`;
  return `days=${filter.days}`;
}

export interface AnalyticsInsight {
  title: string;
  category: 'volume' | 'quality' | 'intent' | 'cost' | 'other';
  severity: 'info' | 'warning' | 'alert';
  description: string;
}

export interface AnalyticsAnalystReport {
  summary: string;
  highlights: string[];
  insights: AnalyticsInsight[];
  costBreakdown: string;
  recommendations: string[];
  analyzedAt: string;
  model: string;
}

export interface AnalyticsAnalystResponse {
  report: AnalyticsAnalystReport;
  period: { from: string; to: string };
}

export const analyzeAnalytics = async (
  filter: AnalyticsFilter,
): Promise<AnalyticsAnalystResponse> => {
  const params = buildAnalyticsParams(filter);
  const { data } = await apiClient.post<AnalyticsAnalystResponse>(`/analytics/analyze?${params}`);
  return data;
};
